<template>
  <DailyScheduleProvider :groupings="groupings" :jobs="filteredJobs" :now="now">
    <template #default="{ items, groups, options }">
      <div class="timeline">
        <DailyScheduleNavigator :range="range" @rangechanged="onRangeChanged" />

        <DailyScheduleTimeline
          ref="timeline"
          :groups="groups"
          :items="items"
          :options="options"
          :range="range"
          @rangechanged="onRangeChanged"
          @select="onJobSelected"
        />
      </div>
    </template>
  </DailyScheduleProvider>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import moment from 'moment'
import { orderBy } from 'lodash'
import { useStore } from 'vuex-composition-helpers'
import { SET_RANGE } from '@/store/modules/day-view'
import { useScheduleFilterStore } from '@/components/schedule/ScheduleFilterStore'
import { useFilteredJobStoreLegacyAdapter } from '../jobs/job-search-page/filteredJobStore'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { until } from '@vueuse/core'
import DailyScheduleProvider from '@/components/daily-schedule/DailyScheduleProvider'
import DailyScheduleNavigator from '@/components/daily-schedule/DailyScheduleNavigator.vue'
import DailyScheduleTimeline from '@/components/daily-schedule/DailyScheduleTimeline.vue'

const emit = defineEmits<{
  (e: 'select', id: string): void
}>()

const store = useStore()
const filteredJobStore = useFilteredJobStoreLegacyAdapter()
const scheduleFilterStore = useScheduleFilterStore()

const timeline = ref()

const now = ref(moment.utc())
let interval: ReturnType<typeof setInterval> | null = null
const updateFrequency = moment.duration(30, 'seconds').asMilliseconds()

const { data: filterOptions, isFetched: isFilterOptionsFetched } = useGetJobFilterOptions()
const defaultResourceTypeIds = ref<number[]>([])
const defaultDivisionIds = ref<number[]>([])
const defaultServiceProviderIds = ref<(number | null)[]>([])

const range = computed(() => store.state.dayView.range)
const jobs = computed(() => store.getters['jobs/getNonArchivedJobs'])
const getVisibleUnits = computed(() => store.getters['unitNumbers/getVisibleUnits'])
const getActiveResourceTypes = computed(() => store.getters['resourceTypes/getActiveResourceTypes'])
const getDivisionById = computed(() => store.getters['divisions/getById'])
const getCompanyByOrganizationId = computed(() => store.getters['companies/getByOrganizationId'])
const getRoleAssignmentBusinessUnitIds = computed(
  () => store.getters['permissions/getRoleAssignmentBusinessUnitIds'],
)

const {
  divisionFiltersExplicitlySet,
  resourceTypeFiltersExplicitlySet,
  serviceProviderFiltersExplicitlySet,
  showOfflineUnits,
  showStandbyUnits,
  showUnitsWithNoAssignedJobs,
} = scheduleFilterStore

const getDefaultSelection = (
  explicitlySet: boolean,
  currentSelection: any[],
  defaultValues: any[],
) => {
  return !explicitlySet && currentSelection.length === 0 ? defaultValues : currentSelection
}

const selectedDivisions = computed(() =>
  getDefaultSelection(
    divisionFiltersExplicitlySet,
    filteredJobStore.selectedDivisions.value,
    defaultDivisionIds.value,
  ),
)

const selectedResourceTypes = computed(() =>
  getDefaultSelection(
    resourceTypeFiltersExplicitlySet,
    filteredJobStore.selectedResourceTypes.value,
    defaultResourceTypeIds.value,
  ),
)

const selectedServiceProviders = computed(() =>
  getDefaultSelection(
    serviceProviderFiltersExplicitlySet,
    filteredJobStore.selectedServiceProviders.value,
    defaultServiceProviderIds.value,
  ),
)

const filterUnits = (units: any[]) => {
  if (!Array.isArray(units)) return []

  return units.filter((unit) => {
    // Visibility filters
    if (!showOfflineUnits && unit.isOffline) return false
    if (!showStandbyUnits && unit.isStandby) return false
    if (!showUnitsWithNoAssignedJobs && !jobs.value.some((j: any) => j.unitId === unit.id)) {
      return false
    }

    // Division filters
    if (!selectedDivisions.value.length) return false

    const division = getDivisionById.value?.(unit.divisionId)
    const roleAssignmentIds = Array.isArray(getRoleAssignmentBusinessUnitIds.value)
      ? getRoleAssignmentBusinessUnitIds.value
      : []

    const divisionMatch =
      selectedDivisions.value.some((id) => id === division?.uuid) ||
      (unit.divisionId === null && roleAssignmentIds.some((b) => b === unit.ownerId))

    if (!divisionMatch) return false

    // Service provider filters
    if (!selectedServiceProviders.value.length) return false
    return selectedServiceProviders.value.some((id) => id === unit.organizationId)
  })
}

const unitsOverview = (filteredUnits: any[], allUnitsCount: number) => {
  if (allUnitsCount === 0) return 'No resources in this group'

  const counts: string[] = []

  const activeCount = filteredUnits.filter((unit) => unit.isActive).length
  if (activeCount > 0) counts.push(`${activeCount} active`)

  if (showStandbyUnits) {
    const standbyCount = filteredUnits.filter((unit) => unit.isStandby).length
    if (standbyCount > 0) counts.push(`${standbyCount} standby`)
  }

  if (showOfflineUnits) {
    const offlineCount = filteredUnits.filter((unit) => unit.isOffline).length
    if (offlineCount > 0) counts.push(`${offlineCount} offline`)
  }

  const hiddenCount = allUnitsCount - filteredUnits.length
  if (hiddenCount > 0) counts.push(`${hiddenCount} hidden`)

  return counts.join(', ') || 'No units visible'
}

const groupings = computed(() => {
  const units = Object.values(getVisibleUnits.value || {}) as any[]
  const parentGroups = (getActiveResourceTypes.value || []).filter((rt: any) =>
    selectedResourceTypes.value.includes(rt.id),
  )

  return parentGroups.map((group: any) => {
    const unitsForResourceType = units.filter((u) => u.resourceTypeId === group.id)
    const filteredUnits = filterUnits(unitsForResourceType)

    const orderedUnits = orderBy(filteredUnits, [
      'isOffline',
      'isActive',
      'isStandby',
      (r) => getDivisionById.value?.(r.divisionId)?.name || '',
      (r) => getCompanyByOrganizationId.value?.(r.organizationId)?.name || '',
    ])

    return {
      id: `resourceTypeId-${group.id}`,
      group,
      units: orderedUnits,
      unitsOverview: unitsOverview(orderedUnits, unitsForResourceType.length),
    }
  })
})

const filteredJobs = computed(() => {
  const filtered = jobs.value
    .filter((j: any) => selectedDivisions.value.includes(j.ownerId))
    .filter((j: any) => selectedResourceTypes.value.includes(j.resourceTypeId))
    .filter((j: any) => j.isResourceAssigned)

  return orderBy(filtered, ['isLate', 'isOverdue', 'activeTime', 'startTime'])
})

const onJobSelected = ({ items }: { items: string[] }) => {
  if (!items?.length) return

  const [jobItemId] = items
  const match = jobItemId.match(/job-(\d+)/)
  if (match) {
    const [, id] = match
    emit('select', id)
  }
}

const onRangeChanged = ({ start, end }: { start: any; end: any }) => {
  store.commit(`dayView/${SET_RANGE}`, {
    start: moment(start),
    end: moment(end),
  })
}

onMounted(async () => {
  // Setup time update interval
  let frame: number
  interval = setInterval(() => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      now.value = moment.utc()
    })
  }, updateFrequency)

  await until(isFilterOptionsFetched).toBe(true)

  defaultResourceTypeIds.value =
    filterOptions.value?.jobFilterOptions?.resourceTypeOptions?.map((d: any) => d.id) ?? []

  defaultDivisionIds.value =
    filterOptions.value?.jobFilterOptions?.divisionOptions?.map((d: any) => d.id) ?? []

  defaultServiceProviderIds.value = [
    null,
    ...(filterOptions.value?.jobFilterOptions?.serviceProviderOptions?.map((d: any) => d.id) ?? []),
  ]
})

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval)
    interval = null
  }
})
</script>

<style scoped>
.timeline {
  position: relative;
  height: 100%;
}
</style>
